import type { FactoryStructuralUnit } from '../../services/factoryService';

export type ExtractQaItem = {
  question: string;
  answer: string;
  subQuestions?: string[];
  subAnswers?: string[];
  confidence?: number;
  /** Printed problem figure from math-k12-extra; undefined = older extract without field. */
  hasProblemFigure?: boolean;
  figureKind?: string;
  figureNote?: string;
  figureBox?: { x: number; y: number; w: number; h: number };
  /** How figure presence was decided for desk UI. */
  figureSource?: 'extract' | 'inferred' | 'missing';
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function clamp01(value: unknown, fallback = 0): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1, Math.max(0, n));
}

function asBool(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === 'true' || value === '1') return true;
  if (value === false || value === 0 || value === 'false' || value === '0') return false;
  return undefined;
}

function extractFigureMeta(row: Record<string, unknown>): Pick<
  ExtractQaItem,
  'hasProblemFigure' | 'figureKind' | 'figureNote' | 'figureBox' | 'figureSource'
> {
  const kindFinal = String(row.figureKind || row.figure_kind || '').trim();
  const hasFlag = asBool(row.hasProblemFigure) ?? asBool(row.has_problem_figure);
  const hasProblemFigure =
    hasFlag === true || (Boolean(kindFinal) && kindFinal !== 'none')
      ? true
      : hasFlag === false || kindFinal === 'none'
        ? false
        : undefined;
  const boxRaw = asRecord(row.figureBox) || asRecord(row.figure_box);
  const figureBox = boxRaw
    ? {
      x: clamp01(boxRaw.x),
      y: clamp01(boxRaw.y),
      w: clamp01(boxRaw.w),
      h: clamp01(boxRaw.h),
    }
    : undefined;
  const figureNote = String(row.figureNote || row.figure_note || '').trim();
  const figureKind = hasProblemFigure
    ? (kindFinal && kindFinal !== 'none' ? kindFinal : 'illustration')
    : hasProblemFigure === false
      ? 'none'
      : kindFinal || undefined;
  const sourceRaw = String(row.figureSource || row.figure_source || '').trim();
  const figureSource =
    sourceRaw === 'inferred' || sourceRaw === 'extract' || sourceRaw === 'missing'
      ? sourceRaw
      : undefined;
  return {
    ...(hasProblemFigure != null ? { hasProblemFigure } : {}),
    ...(figureKind ? { figureKind } : {}),
    ...(figureNote ? { figureNote } : {}),
    ...(figureBox && (figureBox.w > 0 || figureBox.h > 0) ? { figureBox } : {}),
    ...(figureSource ? { figureSource } : {}),
  };
}

/** Text cues when extract payload predates has_problem_figure. */
export function inferProblemFigureFromText(
  question: string,
  subQuestions: string[] = [],
): boolean {
  const text = [question, ...subQuestions].join('\n');
  return /如图|见图|下图|左图|右图|示意图|数轴|涂色|图形|图\s*[（(]?\d|图\d|下列各图|各图形/.test(text);
}

export function figureKindLabelZh(kind: string | undefined): string {
  const key = String(kind || '').trim();
  if (!key || key === 'none') return '';
  const map: Record<string, string> = {
    geometry_diagram: '几何图',
    table: '表格',
    number_line: '数轴',
    illustration: '插图',
  };
  return map[key] || key.replace(/_/g, ' ');
}

export function qaFigureMarkLabel(qa: ExtractQaItem): {
  tone: 'yes' | 'no' | 'inferred' | 'unknown';
  label: string;
  title?: string;
} {
  if (qa.hasProblemFigure === true && qa.figureSource !== 'inferred') {
    const kind = figureKindLabelZh(qa.figureKind);
    return {
      tone: 'yes',
      label: `有配图 · 嵌入图${kind ? ` · ${kind}` : ''}`,
      title: qa.figureNote || '来自提取 has_problem_figure',
    };
  }
  if (qa.hasProblemFigure === false && qa.figureSource !== 'inferred') {
    return {
      tone: 'no',
      label: '无配图',
      title: '提取 has_problem_figure=false',
    };
  }
  if (qa.figureSource === 'inferred') {
    return {
      tone: 'inferred',
      label: '疑似有配图 · 文案推断',
      title: '提取结果未含 has_problem_figure；按题干文案推断。重新提取后可写实字段。',
    };
  }
  return {
    tone: 'unknown',
    label: '无配图字段',
    title: '当前颗粒提取快照没有 has_problem_figure / figure_kind。需用已升级的 math-k12-extra 重新提取本页。',
  };
}

function readRawExtractQaRows(unit: FactoryStructuralUnit): Record<string, unknown>[] {
  const root = asRecord(unit.extractResult);
  if (!root) return [];
  const analysisWrap = asRecord(root.analysis);
  const particles =
    asRecord(analysisWrap?.particles)
    || asRecord(asRecord(analysisWrap?.analysis)?.particles)
    || asRecord(root.particles)
    || asRecord(asRecord(root.parsed)?.Questions_Answers ? root.parsed : null);
  const qaRaw = Array.isArray(particles?.Questions_Answers)
    ? particles!.Questions_Answers
    : Array.isArray(analysisWrap?.Questions_Answers)
      ? analysisWrap!.Questions_Answers
      : Array.isArray(asRecord(root.parsed)?.Questions_Answers)
        ? (asRecord(root.parsed)!.Questions_Answers as unknown[])
        : [];
  return qaRaw.map((raw) => asRecord(raw) || {});
}

function finalizeFigureFields(
  meta: Pick<ExtractQaItem, 'hasProblemFigure' | 'figureKind' | 'figureNote' | 'figureBox' | 'figureSource'>,
  question: string,
  subQuestions: string[],
): Pick<ExtractQaItem, 'hasProblemFigure' | 'figureKind' | 'figureNote' | 'figureBox' | 'figureSource'> {
  const note = String(meta.figureNote || '');
  const looksLikeStemInference = /题干含图示|序列化补全|normalize\s*补全/.test(note);

  // 题干补全优先于服务端 figureSource，避免「补全 + 幻觉 box」混进试验台
  if (looksLikeStemInference || meta.figureSource === 'inferred') {
    return {
      ...meta,
      hasProblemFigure: meta.hasProblemFigure === false ? false : true,
      figureKind: meta.figureKind || 'illustration',
      figureSource: 'inferred',
    };
  }

  if (meta.figureSource === 'extract' || meta.figureSource === 'missing') {
    return { ...meta, figureSource: meta.figureSource };
  }

  const hasBox = Boolean(meta.figureBox && (meta.figureBox.w > 0 || meta.figureBox.h > 0));
  if (meta.hasProblemFigure != null) {
    return { ...meta, figureSource: 'extract' };
  }
  if (inferProblemFigureFromText(question, subQuestions)) {
    return {
      hasProblemFigure: true,
      figureKind: 'illustration',
      figureSource: 'inferred',
      figureNote: '题干含图示文案；提取快照无 has_problem_figure',
      ...(hasBox ? { figureBox: meta.figureBox } : {}),
    };
  }
  return { figureSource: 'missing' };
}

/**
 * 插图提取试验台 / 切块预览：仅「提取确认的印刷配图」。
 * 排除题干推断、补全文案、以及过小的幻觉 figure_box。
 * 任务归属由结构单元 batchId 约束（调用方过滤），不做标题启发式。
 */
export function isConfirmedPrintedFigureQa(qa: ExtractQaItem): boolean {
  if (qa.hasProblemFigure !== true) return false;
  if (qa.figureSource === 'inferred' || qa.figureSource === 'missing') return false;
  if (/题干含图示|序列化补全|normalize\s*补全/.test(String(qa.figureNote || ''))) {
    return false;
  }
  const box = qa.figureBox;
  if (!box) return false;
  const w = Number(box.w);
  const h = Number(box.h);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return false;
  // 过小框多半是模型占位/幻觉，不能当可切块配图
  if (w * h < 0.01) return false;
  if (w < 0.04 || h < 0.025) return false;
  return true;
}

/** @deprecated 用 isConfirmedPrintedFigureQa；任务范围由 batchId 过滤 */
export function isFigureExtractListCandidate(qa: ExtractQaItem): boolean {
  return isConfirmedPrintedFigureQa(qa);
}

function pickText(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (c && typeof c === 'object' && !Array.isArray(c)) {
      const o = c as Record<string, unknown>;
      // extras_v2 / unified schema uses Question.main / Answer.main
      const nested = pickText(
        o.main,
        o.text,
        o.content,
        o.markdown,
        o.stem,
        o.body,
        o.subQuestion,
        o.subAnswer,
      );
      if (nested) return nested;
    }
  }
  return '';
}

function joinSubParts(
  parts: unknown,
  key: 'subQuestion' | 'subAnswer',
): string {
  if (!Array.isArray(parts) || !parts.length) return '';
  return parts
    .map((raw, i) => {
      const row = asRecord(raw) || {};
      const text = pickText(row[key], row);
      return text ? `${i + 1}. ${text}` : '';
    })
    .filter(Boolean)
    .join('\n');
}

function formatQuestion(row: Record<string, unknown>): string {
  const q = asRecord(row.Question) || asRecord(row.question);
  const main = pickText(q?.main, q, row.question, row.Question, row.stem);
  return main;
}

function formatAnswer(row: Record<string, unknown>): string {
  const a = asRecord(row.Answer) || asRecord(row.answer);
  const main = pickText(a?.main, a, row.answer, row.Answer);
  return main;
}

function extractSubQuestions(row: Record<string, unknown>): string[] {
  const q = asRecord(row.Question) || asRecord(row.question);
  const subQuestionsRaw = q?.subQuestions;
  if (!Array.isArray(subQuestionsRaw) || !subQuestionsRaw.length) return [];
  return subQuestionsRaw
    .map((raw) => {
      const item = asRecord(raw) || {};
      return pickText(item.subQuestion, item);
    })
    .filter(Boolean);
}

function extractSubAnswers(row: Record<string, unknown>): string[] {
  const a = asRecord(row.Answer) || asRecord(row.answer);
  const subAnswersRaw = a?.subAnswers;
  if (!Array.isArray(subAnswersRaw) || !subAnswersRaw.length) return [];
  return subAnswersRaw
    .map((raw) => {
      const item = asRecord(raw) || {};
      return pickText(item.subAnswer, item);
    })
    .filter(Boolean);
}

/** Read Questions_Answers from extractQa (preferred) or stored extractResult. */
export function structuralUnitExtractQa(unit: FactoryStructuralUnit): ExtractQaItem[] {
  const rawRows = readRawExtractQaRows(unit);

  if (Array.isArray(unit.extractQa) && unit.extractQa.length) {
    return unit.extractQa.map((row, index) => {
      const question = String(row.question || '（无题干）');
      const subQuestions = Array.isArray(row.subQuestions) ? row.subQuestions.map(String) : [];
      const fromList = extractFigureMeta(row as Record<string, unknown>);
      const fromRaw = rawRows[index] ? extractFigureMeta(rawRows[index]!) : {};
      const meta = fromList.hasProblemFigure != null ? fromList : fromRaw;
      return {
        question,
        answer: String(row.answer || '（无答案）'),
        subQuestions,
        subAnswers: Array.isArray(row.subAnswers) ? row.subAnswers.map(String) : [],
        confidence: Number.isFinite(Number(row.confidence)) ? Number(row.confidence) : undefined,
        ...finalizeFigureFields(meta, question, subQuestions),
      };
    });
  }

  return rawRows.map((row) => {
    const question = formatQuestion(row) || '（无题干）';
    const subQuestions = extractSubQuestions(row);
    return {
      question,
      answer: formatAnswer(row) || '（无答案）',
      subQuestions,
      subAnswers: extractSubAnswers(row),
      confidence: Number.isFinite(Number(row.confidence)) ? Number(row.confidence) : undefined,
      ...finalizeFigureFields(extractFigureMeta(row), question, subQuestions),
    };
  });
}

/** True when desk should treat this unit as extract-failed (ignore stale errors if QA exists). */
export function structuralUnitLooksExtractFailed(unit: FactoryStructuralUnit): boolean {
  if (unit.extractIgnored || unit.extractStatus === 'ignored') return false;
  if (unit.extractStatus === 'succeeded') return false;
  const qaCount =
    structuralUnitExtractQa(unit).length
    || Number(unit.extractSummary?.qaCount)
    || 0;
  if (qaCount > 0) return false;
  return (
    unit.extractStatus === 'failed'
    || Boolean(String(unit.extractError || '').trim())
  );
}

export function structuralUnitExtractTitle(unit: FactoryStructuralUnit): string {
  if (unit.extractTitle) return String(unit.extractTitle);
  if (unit.extractSummary?.title) return String(unit.extractSummary.title);
  const root = asRecord(unit.extractResult);
  const analysisWrap = asRecord(root?.analysis);
  const inner = asRecord(analysisWrap?.analysis) || analysisWrap;
  return pickText(inner?.title, inner?.brief);
}

/** Per-unit token label from extractSummary / extractResult.metadata.usage. */
export function structuralUnitExtractTokensLabel(unit: FactoryStructuralUnit): string {
  const summaryUsage = unit.extractSummary?.usage;
  const meta = unit.extractResult?.metadata;
  const metaUsage =
    meta && typeof meta === 'object' && !Array.isArray(meta)
      ? (meta as { usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } }).usage
      : undefined;
  const usage = summaryUsage || metaUsage;
  if (!usage) return '';
  const prompt = Number(usage.prompt_tokens);
  const completion = Number(usage.completion_tokens);
  const total = Number(usage.total_tokens);
  if (Number.isFinite(total) && total > 0) return `${Math.round(total)} tok`;
  if (Number.isFinite(prompt) || Number.isFinite(completion)) {
    const p = Number.isFinite(prompt) ? prompt : 0;
    const c = Number.isFinite(completion) ? completion : 0;
    return `${Math.round(p)}+${Math.round(c)} tok`;
  }
  return '';
}

/** Chip label for extract route (never imply retired block-stream by default). */
export function structuralUnitExtractRouteLabel(
  unit: FactoryStructuralUnit,
  opts?: { primaryDiscipline?: string | null },
): string {
  const target = String(unit.extractTarget || '').trim();
  if (target) {
    if (/math[_-]?k12[_-]?extra/i.test(target)) return 'math-extra';
    if (/block.?stream/i.test(target)) return 'extras_v2';
    if (/extras_v2|image_url/i.test(target)) return 'extras_v2';
    if (/page_direct/i.test(target) && opts?.primaryDiscipline === 'math') return 'math-extra';
    if (/page_direct/i.test(target)) return 'extras_v2';
    return target;
  }
  const precursor = unit.extractPrecursor as { target_fc?: string; input_kind?: string; mode?: string } | null;
  if (precursor?.target_fc && /math[_-]?k12[_-]?extra/i.test(precursor.target_fc)) {
    return 'math-extra';
  }
  if (precursor?.target_fc && /extras/i.test(precursor.target_fc)) return 'extras_v2';
  if (opts?.primaryDiscipline === 'math') return 'math-extra';
  if (precursor?.mode === 'page_direct' || precursor?.input_kind === 'image_url') return 'extras_v2';
  if (precursor?.target_fc) {
    if (/block.?stream/i.test(precursor.target_fc)) return 'extras_v2';
  }
  return opts?.primaryDiscipline === 'math' ? 'math-extra' : 'extras_v2';
}
